import { BlockOutlined, ChevronRight } from '@mui/icons-material';
import {
    CardContent,
    Chip,
    Grid,
    IconButton,
    ListItemText,
    Pagination,
    Paper,
    Stack,
    TextField,
} from '@mui/material';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFetch } from '../../../hooks/useFecth';
import { DatePicker } from '@mui/x-date-pickers';

export default function OrdersSocialSoul() {
    const [searchParams, setParams] = useSearchParams();

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const { data, mutate } = useFetch('brokers/lomadee/orders', {
        params: {
            ...Object.fromEntries(searchParams),
        },
    });

    const handlePage = (event, page) => {
        setParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            newParams.set('page', page);
            return newParams;
        });
    };

    const handleDateFilter = () => {
        setParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            if (startDate) {
                newParams.set('startDate', startDate.toISOString());
            } else {
                newParams.delete('startDate');
            }
            if (endDate) {
                newParams.set('endDate', endDate.toISOString());
            } else {
                newParams.delete('endDate');
            }
            newParams.set('page', 1);
            return newParams;
        });
    };

    const formatPrice = price => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(price);
    };

    const formatDate = dateString => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return 'Data inválida';
        }
    };

    const getStatusColor = status => {
        switch (status?.toLowerCase()) {
            case 'approved':
            case 'paid':
                return 'success';
            case 'pending':
                return 'warning';
            case 'cancelled':
            case 'rejected':
                return 'error';
            default:
                return 'default';
        }
    };

    return (
        <Container maxWidth="xl">
            <Stack pb={2}>
                <Typography variant="h2">Pedidos Lomadee</Typography>
                <Typography variant="body2" color="text.secondary">
                    Acompanhe pedidos e comissões da rede Lomadee
                </Typography>
            </Stack>

            <Card>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <DatePicker
                                label="Data inicial"
                                value={startDate}
                                onChange={newValue => {
                                    setStartDate(newValue);
                                    handleDateFilter();
                                }}
                                slotProps={{
                                    textField: { fullWidth: true },
                                    field: { clearable: true },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <DatePicker
                                label="Data final"
                                value={endDate}
                                onChange={newValue => {
                                    setEndDate(newValue);
                                    handleDateFilter();
                                }}
                                slotProps={{
                                    textField: { fullWidth: true },
                                    field: { clearable: true },
                                }}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
                <Divider />

                <List size="md" sx={{ gap: 1 }}>
                    <>
                        {data?.meta?.total === 0 && (
                            <Paper sx={{ p: 4, border: 0 }}>
                                <Stack direction="row" justifyContent="center" gap={1}>
                                    <Typography>Nenhum pedido encontrado.</Typography>
                                    <BlockOutlined />
                                </Stack>
                            </Paper>
                        )}

                        {data?.orders?.map(item => (
                            <React.Fragment key={`order_item_${item.id}`}>
                                <ListItem>
                                    <ListItemText
                                        primary={`Pedido #${item.id}`}
                                        primaryTypographyProps={{ variant: 'title-md' }}
                                        secondary={
                                            <Stack spacing={0.5} pt={0.5}>
                                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                                                    <Chip size="small" label={getStatusColor(item.status)} color={getStatusColor(item.status)} />
                                                    {item.commission && (
                                                        <Chip
                                                            size="small"
                                                            label={`Comissão: ${formatPrice(item.commission)}`}
                                                            color="primary"
                                                        />
                                                    )}
                                                    {item.value && <Chip size="small" label={`Valor: ${formatPrice(item.value)}`} />}
                                                    {item.brand?.name && <Chip size="small" label={item.brand.name} variant="outlined" />}
                                                </Stack>
                                                <Typography variant="caption" color="text.secondary">
                                                    Data: {formatDate(item.createdAt)}
                                                </Typography>
                                                {item.items && item.items.length > 0 && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {item.items.length} {item.items.length === 1 ? 'item' : 'itens'}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        }
                                    />
                                </ListItem>
                                <Divider />
                            </React.Fragment>
                        ))}
                    </>
                </List>

                {data?.meta?.total > 0 && (
                    <Stack direction="row" py={2} justifyContent="center">
                        <Pagination page={data?.meta?.page || 1} count={data?.meta?.totalPages || 1} onChange={handlePage} />
                    </Stack>
                )}
            </Card>
        </Container>
    );
}

